import { motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Globe,
  Heart,
  MapPin,
  PartyPopper,
  Palette,
  Sparkles,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "../assets/hero-image.webp";
import heroImageMobile from "../assets/hero-image-mobile.webp";
import eventImg from "../assets/event-coverage.webp";
import framingImg from "../assets/picture-framing.jpg";
import albumImg from "../assets/albums.webp";
import serviceStudioShoot from "../assets/studio-images/studio-image21.webp";
import introFamily from "../assets/studio-images/studio-image40.webp";
import introPortrait from "../assets/studio-images/studio-image08.webp";
import { studioImageAlt } from "../studioImageAlts";
import ReviewsSection from "../components/ReviewsSection";
import FAQSection from "../components/FAQSection";
import CtaBand from "../components/CtaBand";
import printsBannerBg from "../assets/prints-banner-bg.webp";
import heroShop from "../assets/heroes/hero-shop.webp";
import HomeGallerySection from "../components/HomeGallerySection";
import SEO from "../components/SEO";
import { PAGES } from "../seoContent";

const PHOTO_SERVICES = [
  {
    Icon: User,
    title: "Portrait Photography",
    text: "Relaxed, guided studio sessions, from portraits to personal branding.",
    cta: "Plan your portrait session",
    to: "/portrait-photographer-rochdale",
  },
  {
    Icon: Users,
    title: "Family Photography",
    text: "Genuine, relaxed family portraits, from small groups to generations.",
    cta: "Plan your family session",
    to: "/family-photographer-rochdale",
  },
  {
    Icon: PartyPopper,
    title: "Birthday & Celebrations",
    text: "Parties and milestone birthdays, captured as they happen.",
    cta: "Book birthday coverage",
    to: "/packages",
  },
  {
    Icon: Heart,
    title: "Wedding Photography",
    text: "Natural coverage of the emotions and details of your big day.",
    cta: "Choose a wedding package",
    to: "/packages",
  },
  {
    Icon: CalendarDays,
    title: "Event Photography",
    text: "Anniversaries, baby showers, corporate events and more, across the UK.",
    cta: "Book event coverage",
    to: "/packages",
  },
  {
    Icon: Briefcase,
    title: "Headshots & Personal Branding",
    text: "Headshots for LinkedIn, websites and social media, at our Rochdale studio.",
    cta: "Book a headshot session",
    to: "/book",
  },
];

const WHY_CHOOSE = [
  {
    Icon: Sparkles,
    title: "A welcoming experience",
    text: "Relaxed, guided sessions.",
  },
  {
    Icon: MapPin,
    title: "A dedicated studio",
    text: "Beechwood House, Milnrow Road, Rochdale.",
  },
  {
    Icon: UserCheck,
    title: "Tailored to you",
    text: "Every session shaped around you.",
  },
  {
    Icon: Palette,
    title: "Professional editing",
    text: "A polished, natural finish.",
  },
  {
    Icon: Globe,
    title: "Beyond the studio",
    text: "Events and location shoots across the UK.",
  },
];

const services = [
  {
    title: "Studio Photography",
    desc: "Professional studio shoots for individuals, families and brands, tailored to you.",
    cta: "Book a studio session →",
    img: serviceStudioShoot,
    pos: "object-top",
    to: "/book",
  },
  {
    title: "Event Coverage",
    desc: "Photo and video coverage that captures every special occasion, big or small.",
    cta: "Book event coverage →",
    img: eventImg,
    to: "/packages",
  },
  {
    title: "Albums & Prints",
    desc: "Premium photo albums and professional prints made to last a lifetime.",
    cta: "Order prints →",
    img: albumImg,
    to: "/printing",
  },
  {
    title: "Bespoke Picture Framing",
    desc: "Custom frames to display your favourite photos beautifully, your way.",
    cta: "Shop frames →",
    img: framingImg,
    to: "/shop",
  },
];

// four questions here; the full list lives on the Contact page
const HOME_FAQS = [0, 1, 2, 4].map((i) => PAGES["/"].faqs[i]);

export default function Home() {
  return (
    <>
      <SEO
        title={PAGES["/"].title}
        description={PAGES["/"].description}
      />
      <div>
        {/* HERO */}
        <section className="relative flex min-h-[100dvh] items-center overflow-hidden">
          <picture className="absolute inset-0">
            <source media="(max-width: 767px)" srcSet={heroImageMobile} />
            <img
              src={heroImage}
              alt=""
              fetchPriority="high"
              className="h-full w-full object-cover object-right md:object-center"
            />
          </picture>

          <div className="absolute inset-0 bg-[#1c1c1c]/70 md:bg-gradient-to-r md:from-[#1c1c1c]/90 md:via-[#1c1c1c]/55 md:to-[#1c1c1c]/5" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1c1c1c]/70 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#1c1c1c]/60 to-transparent" />

          <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-28 sm:px-10 lg:px-16 xl:px-10">
            <div className="flex flex-col items-start">
              <div className="mt-6 max-w-4xl text-left">
                <motion.p
                  className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-pink-400 sm:text-sm sm:tracking-[0.35em]"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  Welcome to Kinews Studio Limited
                </motion.p>
                <motion.h1
                  className="text-3xl font-semibold leading-[1.25] tracking-tight sm:text-4xl md:text-5xl md:leading-[1.25]"
                  initial={{ opacity: 0, y: 22, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 1, delay: 0.1 }}
                >
                  Professional Photographer &amp; Photography Studio in Rochdale
                </motion.h1>

              </div>

              <motion.div
                className="mt-10 flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.75, ease: "easeOut" }}
              >
                <Link
                  to="/book"
                  className="inline-flex rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95 items-center gap-2"
                >
Book a Studio Session
<ArrowRight size={18} aria-hidden="true" />
</Link>

                <Link
                  to="/packages"
                  className="inline-flex rounded-full border-2 border-white bg-transparent px-7 py-3 text-[15px] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-[#1c1c1c] active:scale-95 items-center gap-2"
                >
Book Event Coverage
<ArrowRight size={18} aria-hidden="true" />
</Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION */}
        <section className="relative overflow-hidden bg-[#fff7fb]">
          <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-pink-200/40 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-6 py-12 sm:px-10 md:py-14 lg:grid-cols-2 lg:gap-16 lg:px-16 xl:px-10">
            <div>
              <h2 className="text-balance text-2xl font-semibold leading-tight tracking-tight text-black md:text-4xl md:leading-[1.2]">
                Capturing Your Moments,{" "}
                <span className="text-pink-600">Creating Lasting Memories</span>
              </h2>

              <p className="mt-5 max-w-xl text-pretty text-[15px] leading-relaxed text-black/70 md:text-base">
                Studio portraits, family shoots, birthdays, weddings and special
                events in Rochdale and Greater Manchester. We capture genuine
                moments with care, creativity and attention to detail.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95"
                >
                  Book Your Session
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <Link
                  to="/rentals"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-pink-500 px-7 py-3 text-[15px] font-semibold text-pink-600 transition hover:-translate-y-0.5 hover:bg-pink-500 hover:text-white active:scale-95"
                >
                   Rent Our Studio
                 <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="relative mx-auto h-[320px] w-full max-w-md sm:h-[400px] lg:h-[400px] lg:max-w-none">
              <div className="absolute inset-y-0 right-0 w-[64%] overflow-hidden rounded-[2rem] border border-black/5 bg-white shadow-[0_24px_60px_rgba(0,0,0,0.18)]">
                <img
                  src={introFamily}
                  alt={studioImageAlt(39)}
                  loading="lazy"
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="absolute left-0 top-10 h-[52%] w-[42%] overflow-hidden rounded-[1.75rem] border-4 border-white shadow-[0_20px_50px_rgba(0,0,0,0.2)]">
                <img
                  src={introPortrait}
                  alt={studioImageAlt(7)}
                  loading="lazy"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-20 lg:px-16 xl:px-10">
            <div className="text-center">
              <p className="text-sm uppercase tracking-[0.3em] text-pink-500/80">
                Our Services
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-black md:text-4xl">
                Bringing your special memories to life.
              </h2>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {services.map((s) => (
                <Link
                  key={s.title}
                  to={s.to}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)] transition duration-300 hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_24px_50px_rgba(236,72,153,0.15)]"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#fff7fb]">
                    <img
                      src={s.img}
                      alt={s.title}
                      className={`h-full w-full object-cover ${s.pos || "object-center"} transition duration-700 group-hover:scale-105`}
                      loading="lazy"
                    />
                  </div>

                  <div className="flex flex-1 flex-col gap-4 p-6">
                    <h3 className="text-lg font-semibold leading-snug text-black">
                      {s.title}
                    </h3>
                    <p className="-mt-2 min-h-[4.25rem] flex-1 text-pretty text-sm leading-relaxed text-black/65">
                      {s.desc}
                    </p>
                    <span className="mt-auto text-sm font-semibold text-pink-600 transition group-hover:text-pink-500 group-hover:underline group-hover:underline-offset-4">
                      {s.cta}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE */}
        <section className="relative overflow-hidden bg-[#1c1c1c] text-white">
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-pink-500/10 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-6 py-12 sm:px-10 md:py-14 lg:grid-cols-[0.7fr_1.6fr] lg:gap-12 lg:px-16 xl:px-10">
            <div>
              <h2 className="text-balance text-2xl font-semibold leading-tight tracking-tight md:text-3xl">
                Why Choose Kinews Studio?
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {WHY_CHOOSE.map(({ Icon, title, text }, index) => (
                <div
                  key={title}
                  className={[
                    "flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-pink-500/40 hover:bg-white/[0.07]",
                    index === WHY_CHOOSE.length - 1 && WHY_CHOOSE.length % 2
                      ? "sm:col-span-2"
                      : "",
                  ].join(" ")}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-pink-500/15 text-pink-400">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold leading-snug">
                      {title}
                    </h3>
                    <p className="mt-1 text-pretty text-sm leading-relaxed text-white/65">
                      {text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <HomeGallerySection />

        {/* PRINTS CTA */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 md:py-16 lg:px-16 xl:px-10">
            <CtaBand
              heading="Professional Printing Services in Rochdale"
              text="Business cards, flyers, posters, stickers and large format banners, printed to a high standard."
              to="/printing"
              label="Order Prints Now"
              image={printsBannerBg}
            />
          </div>
        </section>

        <ReviewsSection />

        {/* PHOTOGRAPHY SERVICES */}
        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-24 lg:px-16 xl:px-10">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-balance text-3xl font-semibold leading-tight tracking-tight text-black md:text-4xl">
                Professional Photography Services in Rochdale &amp; Greater
                Manchester
              </h2>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {PHOTO_SERVICES.map(({ Icon, title, text, cta, to }) => (
                <Link
                  key={title}
                  to={to}
                  className="group relative flex flex-col overflow-hidden rounded-3xl border border-black/10 bg-white p-7 shadow-[0_16px_40px_rgba(0,0,0,0.06)] transition duration-300 hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_24px_50px_rgba(236,72,153,0.15)]"
                >
                  <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-pink-100/70 transition duration-500 group-hover:scale-150" />

                  <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-pink-500 text-white shadow-[0_10px_24px_rgba(236,72,153,0.35)]">
                    <Icon size={26} aria-hidden="true" />
                  </span>

                  <h3 className="relative mt-6 text-xl font-semibold leading-snug text-black">
                    {title}
                  </h3>
                  <p className="relative mt-3 flex-1 text-pretty text-[15px] leading-relaxed text-black/65">
                    {text}
                  </p>
                  <span className="relative mt-6 inline-flex items-center gap-2 text-sm font-semibold text-pink-600 transition group-hover:gap-3 group-hover:text-pink-500">
                    {cta}
                    <ArrowRight size={16} aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>


        <FAQSection items={HOME_FAQS} />

        {/* SHOP CTA */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 md:py-16 lg:px-16 xl:px-10">
            <CtaBand
              heading="We Offer Custom Photo Frames, Canvas & Personalised Gifts"
              to="/shop"
              label="Shop Frames & Gifts"
              image={heroShop}
            />
          </div>
        </section>
      </div>
    </>
  );
}
